import {
  AGENT_MODEL_OPTIONS,
  CANONICAL_PUBLIC_KB_IDS,
  CAPABILITY_BINDING_OPTIONS,
  type VectorKbKnowledgeSource,
} from "../types";

export type CatalogOrgRef = {
  id: string;
  name: string;
  category: VectorKbKnowledgeSource["category"];
};

const CATEGORY_ORDER: VectorKbKnowledgeSource["category"][] = [
  "public",
  "internal",
  "personal",
];

export function getAgentModelLabel(modelName: string): string {
  return (
    AGENT_MODEL_OPTIONS.find((model) => model.value === modelName)?.label ??
    modelName
  );
}

export function getCapabilityBindingLabel(
  binding: string | undefined,
): string {
  if (!binding) {
    return "No binding";
  }
  return (
    CAPABILITY_BINDING_OPTIONS.find((option) => option.value === binding)
      ?.label ?? binding
  );
}

export function formatAccessLabel(access: string | undefined): string {
  if (!access) {
    return "Read";
  }
  return access.charAt(0).toUpperCase() + access.slice(1);
}

export function formatCategoryLabel(category: string): string {
  if (!category) {
    return "";
  }
  return category.charAt(0).toUpperCase() + category.slice(1);
}

export function knowledgeSourceOptionValue(
  source: Pick<VectorKbKnowledgeSource, "id" | "category">,
): string {
  return `${source.category}::${source.id}`;
}

export function parseKnowledgeSourceOptionValue(
  value: string,
): VectorKbKnowledgeSource | undefined {
  const separator = value.indexOf("::");
  if (separator <= 0) {
    return undefined;
  }
  const category = value.slice(
    0,
    separator,
  ) as VectorKbKnowledgeSource["category"];
  const id = value.slice(separator + 2);
  if (!CATEGORY_ORDER.includes(category) || !id) {
    return undefined;
  }
  return { id, category };
}

export function buildKnowledgeSourceOptions(
  orgs: CatalogOrgRef[],
): CatalogOrgRef[] {
  const seen = new Set(orgs.map((org) => `${org.category}::${org.id}`));
  const canonical: CatalogOrgRef[] = CANONICAL_PUBLIC_KB_IDS.filter(
    (id) => !seen.has(`public::${id}`),
  ).map((id) => ({ id, name: id, category: "public" }));
  return [...orgs, ...canonical];
}

export function groupKnowledgeSourceOptions(
  options: CatalogOrgRef[],
): { category: VectorKbKnowledgeSource["category"]; orgs: CatalogOrgRef[] }[] {
  return CATEGORY_ORDER.flatMap((category) => {
    const orgs = options
      .filter((option) => option.category === category)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, "en-NZ"));
    return orgs.length > 0 ? [{ category, orgs }] : [];
  });
}

export function getKnowledgeSourceLabel(
  source: VectorKbKnowledgeSource,
  orgs: CatalogOrgRef[],
): string | undefined {
  if (!source.id) {
    return undefined;
  }
  const org = orgs.find(
    (candidate) =>
      candidate.id === source.id && candidate.category === source.category,
  );
  if (org) {
    return `${org.name} (${formatCategoryLabel(org.category)})`;
  }
  if (source.category === "public") {
    return source.id;
  }
  return undefined;
}

export function getKnowledgeSourceSummary(
  sources: VectorKbKnowledgeSource[],
  orgs: CatalogOrgRef[] = [],
): string {
  if (sources.length === 0) {
    return "No knowledge sources";
  }

  const parts: string[] = [];
  let unresolvedInternal = 0;
  let unresolvedPersonal = 0;

  for (const source of sources) {
    const label = getKnowledgeSourceLabel(source, orgs);
    if (label) {
      parts.push(label);
      continue;
    }
    if (source.category === "internal") {
      unresolvedInternal += 1;
    } else if (source.category === "personal") {
      unresolvedPersonal += 1;
    }
  }

  if (unresolvedInternal > 0) {
    parts.push(
      unresolvedInternal === 1
        ? "1 internal organisation"
        : `${unresolvedInternal} internal organisations`,
    );
  }
  if (unresolvedPersonal > 0) {
    parts.push(
      unresolvedPersonal === 1
        ? "1 personal knowledge base"
        : `${unresolvedPersonal} personal knowledge bases`,
    );
  }

  return parts.length > 0 ? parts.join(" · ") : "No knowledge sources";
}
